import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { toast } from "sonner";
import { useLocalStorage } from "@domun/component-library";
import { Feature } from "@dashboard/constants/features";
import { useFeatureAvailability } from "@dashboard/hooks/feature-availability.hook";
import { FeatureProducts } from "@dashboard/modules/products/constants/features.constants";
import { PlanBenefit } from "@dashboard/entities/plan-benefit";
import { partnerSelector } from "@dashboard/config/redux";
import { checkoutActions } from "@dashboard/modules/checkout/store/checkout.slice";
import { useAnalytics } from "@dashboard/common/analytics";
import {
  FormProps,
  ImageProductForm,
} from "@dashboard/modules/products/types/product-form.types";
import {
  fetchRelatedImagesForFormImage,
  urlsToImageFormItems,
} from "../services/photo-ai.services";

type Step = "closed" | "reference" | "results";

export const usePhotoAi = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logEvent } = useAnalytics();
  const partner = useSelector(partnerSelector);
  const [token] = useLocalStorage("accessToken", "");
  const { values, setFieldValue } = useFormikContext<FormProps>();
  const { isAvailable } = useFeatureAvailability(Feature.PhotoAi, true);
  const { feature: imagesProductsFeature } = useFeatureAvailability(
    FeatureProducts.ProductImages
  );

  const [step, setStep] = useState<Step>("closed");
  const [reference, setReference] = useState<ImageProductForm | null>(null);
  const [relatedUrls, setRelatedUrls] = useState<string[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const images = values.imagesForm || [];

  const maxPhotos = useMemo(() => {
    return (
      (imagesProductsFeature as PlanBenefit)?.conditions
        ?.max_image_by_product || 1
    );
  }, [imagesProductsFeature]);

  const remainingSlots = Math.max(0, maxPhotos - images.length);

  const handleAcquire = () => {
    dispatch(
      checkoutActions.setToBuyItem({
        type: "addon",
        addon_type_id: Feature.PhotoAi,
      })
    );
    setTimeout(() => {
      if (partner?.userId) {
        navigate(`/${partner.userId}/checkout`);
      }
    }, 100);
  };

  const openResults = async (image: ImageProductForm) => {
    if (!token) return;
    setReference(image);
    setStep("results");
    setLoading(true);
    setRelatedUrls([]);
    setSelectedUrls([]);
    try {
      const urls = await fetchRelatedImagesForFormImage(token, image);
      setRelatedUrls(urls);
      logEvent("view_screen", { event_subtype: "photo_ai" });
    } catch {
      toast.error(t("productComponent.product.photoAi.error"));
      setStep("closed");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    logEvent("select_button", { event_subtype: "photo_ai" });
    if (!isAvailable) {
      handleAcquire();
      return;
    }
    if (remainingSlots <= 0) {
      toast.error(t("productComponent.product.photoAi.noSlots"));
      return;
    }
    if (images.length === 1) {
      openResults(images[0]);
      return;
    }
    setReference(images[0] || null);
    setStep("reference");
  };

  const handleClose = () => {
    setStep("closed");
    setReference(null);
    setRelatedUrls([]);
    setSelectedUrls([]);
    setLoading(false);
    setAdding(false);
  };

  const handleConfirmReference = () => {
    if (!reference) return;
    openResults(reference);
  };

  const toggleUrl = (url: string) => {
    setSelectedUrls((prev) => {
      if (prev.includes(url)) {
        return prev.filter((item) => item !== url);
      }
      if (prev.length >= remainingSlots) {
        toast.error(t("productComponent.product.photoAi.noSlots"));
        return prev;
      }
      return [...prev, url];
    });
  };

  const handleAdd = async () => {
    if (!selectedUrls.length || adding) return;
    if (selectedUrls.length > remainingSlots) {
      toast.error(t("productComponent.product.photoAi.noSlots"));
      return;
    }
    setAdding(true);
    try {
      const startId = images.length;
      const newItems = await urlsToImageFormItems(selectedUrls, startId);
      setFieldValue("imagesForm", [...images, ...newItems]);
      toast.success(t("productComponent.product.photoAi.addSuccess"));
      handleClose();
    } catch {
      toast.error(t("productComponent.product.photoAi.addError"));
    } finally {
      setAdding(false);
    }
  };

  const getPreviewSrc = (image: ImageProductForm): string => {
    if (image.type === "url" && typeof image.image === "string") {
      return image.image;
    }
    if (image.type === "file" && image.image instanceof File) {
      return URL.createObjectURL(image.image);
    }
    return "";
  };

  return {
    isAvailable,
    step,
    images,
    reference,
    relatedUrls,
    selectedUrls,
    loading,
    adding,
    remainingSlots,
    setReference,
    handleOpen,
    handleClose,
    handleConfirmReference,
    toggleUrl,
    handleAdd,
    handleAcquire,
    getPreviewSrc,
  };
};
